import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, CheckCircle2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { formatApiErrorDetail } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { PasswordInput } from "@/components/PasswordInput";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const { t } = useT();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Min 8"); return; }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate("/"), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-[#fdfbf7]" data-testid="reset-password-page">
      <div className="absolute top-5 right-5"><LanguageSwitcher /></div>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white rounded-3xl border border-[#e5dfd3] card-shadow p-8">
        <div className="flex items-center gap-2 mb-6">
          <img src="/logo.png" alt="Deuro" className="w-10 h-10 rounded-xl" />
          <span className="font-head font-extrabold text-xl">Deuro</span>
        </div>

        {done ? (
          <div className="text-center py-6" data-testid="reset-success">
            <div className="w-16 h-16 rounded-full bg-[#d1fae5] flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="w-9 h-9 text-[#059669]" />
            </div>
            <p className="font-head font-bold text-lg">{t("reset_done")}</p>
          </div>
        ) : !token ? (
          <p className="text-[#d9532f]" data-testid="reset-no-token">Link non valido.</p>
        ) : (
          <>
            <h1 className="font-head text-2xl font-bold tracking-tight">{t("reset_title")}</h1>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <label className="block">
                <span className="text-sm font-medium">{t("new_password")}</span>
                <div className="mt-1.5">
                  <PasswordInput inputTestId="new-password-input" toggleTestId="new-password-toggle" showLabel={t("show_password")} hideLabel={t("hide_password")}
                    icon={Lock} required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="deuro-input" placeholder="••••••••" autoComplete="new-password" />
                </div>
              </label>
              {error && <div data-testid="reset-error" className="text-sm text-[#d9532f] bg-[#fdeae5] rounded-lg px-3 py-2">{error}</div>}
              <button type="submit" disabled={loading} data-testid="reset-submit"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[#1e3326] text-white font-semibold hover:bg-[#284635] active:scale-[0.99] transition-all disabled:opacity-60">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t("reset_password_btn")} <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
